import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { MapPin, Check, ExternalLink, Navigation, Clock, Route } from "lucide-react";

function FitBounds({ stops }) {
  const map = useMap();
  useEffect(() => {
    if (stops.length === 0) return;
    const bounds = L.latLngBounds(stops.map(s => [s.lat, s.lng]));
    map.fitBounds(bounds, { padding: [30, 30], maxZoom: 13 });
  }, [stops, map]);
  return null;
}

const pinIcon = (num, color, claimed) => L.divIcon({
  className: "custom-pin",
  html: `<div style="width:28px;height:28px;background:${claimed ? '#10b981' : color};border:2.5px solid white;border-radius:50%;display:flex;align-items:center;justify-content:center;color:white;font-size:11px;font-weight:800;box-shadow:0 2px 8px rgba(0,0,0,.25)">${claimed ? '&#10003;' : num}</div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

function formatDuration(seconds) {
  if (!seconds) return "";
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem > 0 ? `${hrs}h ${rem}m` : `${hrs}h`;
}

function formatDistance(meters) {
  if (!meters) return "";
  const miles = meters / 1609.34;
  return `${miles.toFixed(1)} mi`;
}

export default function RoutePreviewMap({ stops, onToggleClaim, onNavigate, themeColor = "#ec4899" }) {
  const stopsWithCoords = (stops || []).filter(s => s.lat && s.lng);
  const [routeCoords, setRouteCoords] = useState([]);
  const [routeInfo, setRouteInfo] = useState(null);

  // Fetch actual road route from OSRM
  useEffect(() => {
    if (stopsWithCoords.length < 2) {
      setRouteCoords(stopsWithCoords.map(s => [s.lat, s.lng]));
      return;
    }

    const coords = stopsWithCoords.map(s => `${s.lng},${s.lat}`).join(";");
    fetch(`https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=geojson`)
      .then(r => r.json())
      .then(data => {
        if (data.code === "Ok" && data.routes?.[0]) {
          const route = data.routes[0];
          const geojsonCoords = route.geometry.coordinates.map(c => [c[1], c[0]]);
          setRouteCoords(geojsonCoords);
          setRouteInfo({
            duration: route.duration,
            distance: route.distance,
          });
        } else {
          // Fallback to straight lines
          setRouteCoords(stopsWithCoords.map(s => [s.lat, s.lng]));
        }
      })
      .catch(() => {
        setRouteCoords(stopsWithCoords.map(s => [s.lat, s.lng]));
      });
  }, [stopsWithCoords.length]);

  if (stopsWithCoords.length === 0) {
    return (
      <div className="h-48 bg-slate-50 rounded-xl border border-dashed border-slate-200 flex flex-col items-center justify-center" data-testid="route-preview-empty">
        <MapPin className="w-6 h-6 text-slate-300 mb-1" />
        <p className="text-[10px] text-slate-400">Route preview will appear after generating</p>
      </div>
    );
  }

  const openFullRoute = () => {
    // Open Google Maps with all waypoints for turn-by-turn navigation
    const waypoints = stopsWithCoords.map(s => `${s.lat},${s.lng}`);
    const origin = waypoints[0];
    const destination = waypoints[waypoints.length - 1];
    const via = waypoints.slice(1, -1).join("|");
    const url = via
      ? `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&waypoints=${via}&travelmode=driving`
      : `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=driving`;
    window.open(url, "_blank");
  };

  // Sacramento as default center (home market)
  const defaultCenter = stopsWithCoords.length > 0
    ? [stopsWithCoords[0].lat, stopsWithCoords[0].lng]
    : [38.5816, -121.4944];

  return (
    <div data-testid="route-preview-map">
      <div className="rounded-xl overflow-hidden border border-slate-200 shadow-sm" style={{ height: 240 }}>
        <MapContainer center={defaultCenter} zoom={12} style={{ height: "100%", width: "100%" }} scrollWheelZoom={true} zoomControl={true} dragging={true}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OSM' />
          <FitBounds stops={stopsWithCoords} />
          {routeCoords.length > 1 && (
            <Polyline positions={routeCoords} pathOptions={{ color: themeColor, weight: 4, opacity: 0.8 }} />
          )}
          {stopsWithCoords.map((stop, i) => (
            <Marker key={stop.id || i} position={[stop.lat, stop.lng]} icon={pinIcon(i + 1, themeColor, stop.claimed)}>
              <Popup>
                <div className="text-center min-w-[140px]">
                  <p className="font-bold text-sm text-slate-800">{stop.name}</p>
                  {stop.address && <p className="text-[10px] text-slate-400 mt-0.5">{stop.address}</p>}
                  <p className="text-xs text-slate-500 mt-0.5">{stop.offer_title}</p>
                  <p className="text-sm font-bold mt-1" style={{ color: '#10b981' }}>${stop.value_est}</p>
                  <div className="flex gap-1.5 justify-center mt-2">
                    {onToggleClaim && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onToggleClaim(stop.id); }}
                        className={`px-2.5 py-1 text-[10px] font-bold rounded-lg flex items-center gap-1 ${stop.claimed ? 'bg-emerald-100 text-emerald-600' : 'bg-emerald-500 text-white hover:bg-emerald-600'}`}
                        data-testid={`map-claim-${stop.id}`}
                      >
                        <Check className="w-3 h-3" /> {stop.claimed ? "Claimed" : "Claim"}
                      </button>
                    )}
                    {onNavigate && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onNavigate(stop); }}
                        className="px-2.5 py-1 bg-sky-500 text-white text-[10px] font-bold rounded-lg flex items-center gap-1 hover:bg-sky-600"
                        data-testid={`map-nav-${stop.id}`}
                      >
                        <ExternalLink className="w-3 h-3" /> Go
                      </button>
                    )}
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {/* Route info bar */}
      <div className="flex items-center justify-between mt-2 px-1">
        <div className="flex items-center gap-3 text-[10px] text-slate-500">
          {routeInfo && (
            <>
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatDuration(routeInfo.duration)}</span>
              <span className="flex items-center gap-1"><Route className="w-3 h-3" />{formatDistance(routeInfo.distance)}</span>
            </>
          )}
          <span>Tap pin to claim or navigate</span>
        </div>
        {stopsWithCoords.length >= 2 && (
          <button
            onClick={openFullRoute}
            className="flex items-center gap-1 text-[10px] font-bold text-sky-600 hover:text-sky-700"
            data-testid="open-full-route"
          >
            <Navigation className="w-3 h-3" /> Directions
          </button>
        )}
      </div>
    </div>
  );
}
